/**
 * ============================================================================
 * BUTTON COMPONENT - 100% Ready with Animations & Modern Styling
 * ============================================================================
 */

import React from 'react';

/**
 * Button Component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.variant - Button variant (primary, secondary, danger, outline, ghost, success, gradient)
 * @param {string} props.size - Button size (sm, md, lg, xl)
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.type - Button type (button, submit, reset)
 * @param {Function} props.onClick - Click handler
 * @param {string} props.className - Additional classes
 * @param {boolean} props.fullWidth - Full width button
 * @param {React.Component} props.icon - Icon component
 * @param {string} props.iconPosition - Icon position (left, right)
 * @param {boolean} props.animate - Enable hover animations
 * @returns {JSX.Element}
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  type = 'button',
  onClick,
  className = '',
  fullWidth = false,
  icon: Icon,
  iconPosition = 'left',
  animate = true,
  ...props
}) => {
  const baseClasses = `
    inline-flex items-center justify-center
    font-semibold rounded-md
    transition-all duration-200 ease
    focus:outline-none
    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
    relative overflow-hidden
    ${animate ? 'hover:shadow-sm active:scale-[0.98]' : ''}
  `;
  
  /* LinkedIn/Cursor design: primary #0A66C2, heights 36/40/44px, radius 6px, transition 0.2s */
  const variantClasses = {
    primary: 'bg-[#0A66C2] hover:bg-[#004182] text-white focus:ring-2 focus:ring-[#0A66C2] focus:ring-offset-2 active:scale-[0.98]',
    secondary: 'bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#374151] border border-transparent hover:border-[#D1D5DB] focus:ring-2 focus:ring-[#6B7280] focus:ring-offset-2',
    danger: 'bg-[#EF4444] hover:bg-[#DC2626] text-white focus:ring-2 focus:ring-[#EF4444] focus:ring-offset-2 active:scale-[0.98]',
    outline: 'border border-[#E5E7EB] bg-transparent text-[#374151] hover:bg-[#F9FAFB] focus:ring-2 focus:ring-[#0A66C2] focus:ring-offset-2',
    ghost: 'bg-transparent text-[#374151] hover:bg-[#F3F4F6] focus:ring-2 focus:ring-[#6B7280] focus:ring-offset-2',
    success: 'bg-[#10B981] hover:bg-[#059669] text-white focus:ring-2 focus:ring-[#10B981] focus:ring-offset-2 active:scale-[0.98]',
    gradient: 'bg-gradient-to-r from-[#0A66C2] to-[#2563eb] hover:from-[#004182] hover:to-[#1d4ed8] text-white focus:ring-2 focus:ring-[#0A66C2] focus:ring-offset-2',
  };

  const sizeClasses = {
    sm: 'h-9 min-h-[36px] px-3.5 text-sm gap-1.5',
    md: 'h-10 min-h-[40px] px-4 text-sm gap-2',
    lg: 'h-11 min-h-[44px] px-5 text-base gap-2',
    xl: 'h-12 min-h-[48px] px-6 text-base gap-2.5',
  };

  const classes = `
    ${baseClasses}
    ${variantClasses[variant] || variantClasses.primary}
    ${sizeClasses[size] || sizeClasses.md}
    ${fullWidth ? 'w-full' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={isDisabled}
      aria-busy={loading}
      aria-disabled={isDisabled}
      {...props}
    >
      {/* Shimmer effect for gradient variant */}
      {variant === 'gradient' && !isDisabled && (
        <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer pointer-events-none" />
      )}
      
      {loading ? (
        <>
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="relative z-10">{children}</span>
        </>
      ) : (
        <>
          {Icon && iconPosition === 'left' && (
            <Icon className="h-4 w-4 relative z-10" />
          )}
          <span className="relative z-10">{children}</span>
          {Icon && iconPosition === 'right' && (
            <Icon className="h-4 w-4 relative z-10" />
          )}
        </>
      )}
    </button>
  );
};

export default Button;
